// TanStack Query 5 client per ADR-013 §8. Server state lives here; UI state
// lives in Zustand (src/store/ui.ts). Keep the boundary clean — anything
// derived from a backend response goes through Query, never Zustand.

import { QueryClient } from "@tanstack/react-query";

import { EnvelopeError } from "./errors";

const MAX_AUTOMATIC_RETRIES = 2;
const BASE_RETRY_DELAY_MS = 1_000;
const MAX_RETRY_DELAY_MS = 30_000;
const JITTER_RATIO = 0.1;
const MAX_JITTER_MS = 1_000;
const MAX_NATIVE_TIMER_DELAY_MS = 2 ** 31 - 1;
const MAX_AUTOMATIC_RETRY_AFTER_MS = MAX_NATIVE_TIMER_DELAY_MS - MAX_JITTER_MS;

function isValidRetryAfterMs(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    Number.isInteger(value) &&
    value >= 0
  );
}

/**
 * Adds strictly positive, bounded jitter without ever retrying before a
 * server-provided retry hint. The 10%/1s cap avoids synchronized retry
 * bursts without turning a short bounded retry policy into a long tail.
 */
function positiveBoundedJitter(delayMs: number): number {
  const maxJitterMs = Math.min(
    Math.max(1, Math.ceil(delayMs * JITTER_RATIO)),
    MAX_JITTER_MS,
  );
  return Math.max(1, Math.ceil(Math.random() * maxJitterMs));
}

/**
 * Spec 58 retryability: an `EnvelopeError` carries its own classification-
 * derived `retryable` flag (validation/not_found/conflict → false; 5xx/
 * dependency/unavailable → true) — that flag is the authority, never a
 * pattern match on the message string. `EnvelopeError.message` is
 * `${code}: ${message}` for the common (envelope-with-errors[]) case, which
 * never contains the raw HTTP status, so a `/4\d\d/`-style regex over the
 * message silently fails to match and falls through to retrying — wasting
 * 2 retries + their backoff delay on an error that will never succeed.
 * Non-envelope throwables (e.g. a raw network TypeError before a response
 * is even parsed) keep the previous failureCount-only behavior.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (error instanceof EnvelopeError) {
    // A server hint plus its maximum jitter must fit native timers. Do not
    // clamp an oversized hint: that would retry before the server allowed.
    // This predicate prevents TanStack Query from scheduling that retry.
    const retryAfterMs = error.retryAfterMs;
    const hasOversizedRetryHint =
      isValidRetryAfterMs(retryAfterMs) &&
      retryAfterMs > MAX_AUTOMATIC_RETRY_AFTER_MS;
    return (
      error.retryable &&
      !hasOversizedRetryHint &&
      failureCount < MAX_AUTOMATIC_RETRIES
    );
  }
  return failureCount < MAX_AUTOMATIC_RETRIES;
}

/**
 * Shared safe-query retry delay. A retryable envelope's finite, non-negative
 * integer `retry_after_ms` is authoritative: only positive jitter is added,
 * so the client never retries before the server's requested limit. Oversized
 * hints are rejected by shouldRetryQuery rather than being shortened here.
 * Without a usable hint (including raw network errors), use capped exponential
 * backoff with the same bounded positive jitter.
 */
export function retryDelay(failureCount: number, error: unknown): number {
  const retryAfterMs =
    error instanceof EnvelopeError && error.retryable
      ? error.retryAfterMs
      : undefined;

  if (isValidRetryAfterMs(retryAfterMs)) {
    return retryAfterMs + positiveBoundedJitter(retryAfterMs);
  }

  const backoffMs = Math.min(
    BASE_RETRY_DELAY_MS * 2 ** failureCount,
    MAX_RETRY_DELAY_MS,
  );
  return backoffMs + positiveBoundedJitter(backoffMs);
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 30s stale window — most HR data is read-heavy, write-rare.
      // Per-query overrides for hot endpoints (chat, AI streams).
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      // Off by default: HR reads are tab-switch heavy and mostly static —
      // a global focus refetch storms the API. Live surfaces (badge counts,
      // notification inbox) opt in per-query with refetchOnWindowFocus: true.
      refetchOnWindowFocus: false,
      retry: shouldRetryQuery,
      retryDelay,
    },
    mutations: {
      // No automatic mutation retry — every retry must be explicit + idempotent
      // per ADR-002 invariant. Phase 5+ AI staged-changes use idempotency keys.
      retry: false,
    },
  },
});
